import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, TrendingUp, ExternalLink, Clock } from 'lucide-react';

const NewsPage = () => {
  const newsItems = [
    {
      id: 1,
      title: 'USPTO Announces New Fast-Track Trademark Program',
      excerpt: 'The United States Patent and Trademark Office has launched a new expedited processing program that could reduce trademark registration time by up to 50%.',
      date: 'March 18, 2024',
      category: 'USPTO Updates',
      featured: true,
      source: 'USPTO Official',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&h=600&fit=crop'
    },
    {
      id: 2,
      title: 'Supreme Court Ruling Impacts Trademark Infringement Cases',
      excerpt: 'A landmark Supreme Court decision establishes new precedent for evaluating likelihood of confusion in trademark disputes.',
      date: 'March 16, 2024',
      category: 'Legal Updates',
      featured: true,
      source: 'Legal News Today',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop'
    },
    {
      id: 3,
      title: 'Rise in Trademark Filings Hits Record High in 2024',
      excerpt: 'New statistics show a 30% increase in trademark applications compared to last year, driven by e-commerce growth and startup boom.',
      date: 'March 14, 2024',
      category: 'Industry News',
      featured: false,
      source: 'IP Business Review',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'
    },
    {
      id: 4,
      title: 'New International Treaty Simplifies Global Trademark Protection',
      excerpt: 'Over 50 countries sign agreement to streamline international trademark registration process through unified filing system.',
      date: 'March 12, 2024',
      category: 'International',
      featured: false,
      source: 'WIPO News',
      image: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&h=600&fit=crop'
    },
    {
      id: 5,
      title: 'Tech Giants Face Off in Major Trademark Dispute',
      excerpt: 'Two leading technology companies enter high-stakes legal battle over similar brand names in the AI space.',
      date: 'March 10, 2024',
      category: 'Case Studies',
      featured: false,
      source: 'Tech Law Journal',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop'
    },
    {
      id: 6,
      title: 'Changes to Trademark Fee Structure Announced',
      excerpt: 'USPTO reveals updated fee schedule for trademark services, with some reductions for small businesses and startups.',
      date: 'March 8, 2024',
      category: 'USPTO Updates',
      featured: false,
      source: 'USPTO Official',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop'
    },
    {
      id: 7,
      title: 'Social Media Usernames Now Eligible for Trademark Protection',
      excerpt: 'Groundbreaking ruling extends trademark protection to social media handles, creating new opportunities for brand protection.',
      date: 'March 5, 2024',
      category: 'Legal Updates',
      featured: false,
      source: 'Digital IP News',
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=600&fit=crop'
    },
    {
      id: 8,
      title: 'AI-Generated Logos Face Trademark Registration Challenges',
      excerpt: 'Patent office issues new guidelines on registering trademarks created using artificial intelligence tools.',
      date: 'March 3, 2024',
      category: 'Technology',
      featured: false,
      source: 'AI & IP Report',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-[#1a1a1a] to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <TrendingUp className="w-4 h-4" />
              Latest Updates
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Trademark News & Updates
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Stay ahead with the latest trademark law updates, USPTO announcements, and industry developments
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured News */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#168356] to-[#0a5938] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">Featured Stories</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {newsItems.filter(item => item.featured).map((news) => (
              <article
                key={news.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#168356] text-white px-3 py-2 rounded-lg text-sm font-semibold">
                      {news.category}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{news.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ExternalLink className="w-4 h-4" />
                      <span>{news.source}</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#168356] transition-colors">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {news.excerpt}
                  </p>
                  <Link
                    to="#"
                    className="inline-flex items-center gap-2 text-[#168356] font-semibold hover:gap-3 transition-all"
                  >
                    Read Full Story
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* All News */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#168356] to-[#0a5938] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">Recent News</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {newsItems.filter(item => !item.featured).map((news) => (
              <article
                key={news.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold">
                      {news.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    <Calendar className="w-3 h-3" />
                    <span>{news.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#168356] transition-colors line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {news.excerpt}
                  </p>
                  <Link
                    to="#"
                    className="inline-flex items-center gap-1 text-[#168356] font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Read More
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <button className="bg-gradient-to-r from-[#168356] to-[#0a5938] hover:from-[#0a5938] hover:to-[#168356] text-white font-bold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#168356]/30">
            Load More News
          </button>
        </div>
      </div>

      {/* Subscribe Section */}
      <div className="bg-gradient-to-br from-[#168356] via-[#0a5938] to-[#0a5938] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Get News Alerts
          </h2>
          <p className="text-white/90 mb-8">
            Subscribe to receive breaking news and important updates about trademark law
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button className="bg-black hover:bg-gray-900 text-white font-bold px-6 py-3 rounded-lg transition-all duration-300">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
